import React, { useState, useEffect } from 'react'
import useBreakpoint from '#src/hooks/useBreakpoint'
import { Image } from '@deal/components'
import { useTrail, animated } from 'react-spring'
import VideoIcon from './video.svg'
import { SpotlightExpertFragment } from '#src/generated/graphql'
import DepartmentExpertHeadline from '#src/components/DepartmentExpertHeadline'
import styles from './styles.css'

interface ExpertCardsProps {
  isExpertPlayerVisible?: boolean
  featuredExperts: SpotlightExpertFragment[]
  onClickExpertCard: (featuredExpert: SpotlightExpertFragment, idx: number) => void
}

const ExpertCardsAnimation: React.SFC<ExpertCardsProps> = ({
  featuredExperts,
  onClickExpertCard
}) => {
  const animationConfig = { mass: 5, tension: 2000, friction: 200 }
  const cardsTrail = useTrail(featuredExperts.length, {
    config: animationConfig,
    opacity: 1,
    x: 0,
    from: {
      opacity: 0,
      x: 20
    }
  })

  return (
    <div className={styles.cardCarouselTrack}>
      {cardsTrail.map(({ x, opacity }, index) => {
        const expert = featuredExperts[index]

        if (!expert.expertProfile || !expert.expertProfile.portraitVideo) {
          return null
        }

        const isLargerThanSm = useBreakpoint('sm')
        const expertFullName = expert.displayName
        const expertShortName = expert.shortName

        return (
          <animated.div
            key={index}
            className={styles.expertCard}
            style={{
              opacity,
              transform: `translate3d(0, ${x}px, 0)`
            }}
            onClick={() => {
              onClickExpertCard(featuredExperts[index], index)
            }}
          >
            <div className={styles.expertVideo}>
              <Image
                src={expert.expertProfile.portraitVideo.url}
                className={styles.embeddedVideo}
                sizes="(min-width: 768px) 200px, 130px"
              />
              <VideoIcon className={styles.videoIcon} />
              <div className={styles.name}>{isLargerThanSm ? expertFullName : expertShortName}</div>
            </div>
            <div className={styles.headline}>
              <DepartmentExpertHeadline departmentType={expert.department.type} />
            </div>
          </animated.div>
        )
      })}
    </div>
  )
}

const ExpertCards: React.SFC<ExpertCardsProps> = ({ isExpertPlayerVisible, ...rest }) => {
  if (isExpertPlayerVisible) {
    return null
  }

  const [hasComponentMounted, setHasComponentMounted] = useState(false)

  useEffect(() => {
    setHasComponentMounted(true)
  }, [])

  return (
    <div className={styles.cardPosition}>
      <div className={styles.header}>pick an expert to learn more</div>
      <div className={styles.cardCarousel}>
        {hasComponentMounted && <ExpertCardsAnimation {...rest} />}
      </div>
    </div>
  )
}

export default ExpertCards
